import { Component, Inject, ChangeDetectionStrategy, signal } from '@angular/core'
import { FieldTree, form, validate } from '@angular/forms/signals'
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog'

import { annaSelvityspyynnonSumma, SelvityspyynnonTyyppi, Selvityspyynto } from '@lemon/core-firebase/model/selvitettavat'
import { LocalDate } from '@lemon/core/model/common'
import { DateService } from '@lemon/core/service/date.service'
import { StringService } from '@lemon/core/service/string.service'
import { TimestampProviderBase } from '@lemon/core/service/timestamp-provider.interface'
import { arvonMuutokset } from 'app/_jaettu-angular/_lomakkeet/arvon-muutokset'
import { Asiakas } from 'app/_jaettu-lemonator/model/asiakas'
import { Kirjaus, Kirjausrivi } from 'app/_jaettu-lemonator/model/kirjanpito'

export interface SelvityspyynnonLiiteValinta {
  avain: string
  otsikko: string
  pvm?: LocalDate
  summa?: number
  selite?: string
  laskunumero?: string
}

export interface KirjauksenLiitevalinnat {
  tositteet: SelvityspyynnonLiiteValinta[]
  laskut: SelvityspyynnonLiiteValinta[]
  /** Sama päättely kuin backendin tallennuksessa: myös kirjanpitäjän raahaamat ja Holvin tiedostot lasketaan liitteiksi, vaikka niitä ei voi valita selvityspyyntöön */
  onkoLiitteita: boolean
}

export interface KirjausSelvityspyyntoDialogData {
  kirjaus: Kirjaus
  kirjausrivi: Kirjausrivi
  asiakas: Asiakas
  tositteet: SelvityspyynnonLiiteValinta[]
  laskut: SelvityspyynnonLiiteValinta[]
}

interface SelvityspyyntoLomake {
  tyyppi: SelvityspyynnonTyyppi
  viesti: string
}

@Component({
  templateUrl: './kirjaus.selvityspyynto.dialog.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class KirjausSelvityspyyntoDialog {
  lomake: FieldTree<SelvityspyyntoLomake>
  tositteet: SelvityspyynnonLiiteValinta[]
  laskut: SelvityspyynnonLiiteValinta[]
  // Asiakkaan näkemät tapahtuman tiedot tulevat backendissä aina kirjauksesta, joten ne näytetään tässä vain tiedoksi
  pvm: LocalDate
  summa: number
  maksunsaaja: string
  private _valitutTositeAvaimet = new Set<string>()
  private _valitutLaskuAvaimet = new Set<string>()
  private readonly _viestiPuuttuu = signal(false)

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: KirjausSelvityspyyntoDialogData,
    private _dialogRef: MatDialogRef<KirjausSelvityspyyntoDialog>,
    private _stringService: StringService,
    private _timestampService: TimestampProviderBase,
    private _dateService: DateService,
  ) {
    const selvityspyynto = this.data.kirjausrivi.sp
    this.tositteet = this.data.tositteet ?? []
    this.laskut = this.data.laskut ?? []
    this.pvm = this._dateService.numberToLocalDate(this.data.kirjaus.p)
    this.summa = annaSelvityspyynnonSumma(this.data.kirjausrivi)
    this.maksunsaaja = this.data.kirjaus.kirjauksenSaajaTaiMaksaja ?? ''
    for (const avain of selvityspyynto?.tositeAvaimet ?? []) {
      this._valitutTositeAvaimet.add(avain)
    }
    for (const avain of selvityspyynto?.laskuAvaimet ?? []) {
      this._valitutLaskuAvaimet.add(avain)
    }
    this.lomake = form(signal<SelvityspyyntoLomake>({ tyyppi: selvityspyynto?.tyyppi ?? 'lisatietopyynto', viesti: selvityspyynto?.kysymys ?? '' }), polku => {
      validate(polku.viesti, () => (this._viestiPuuttuu() ? { kind: 'required' } : null))
    })
    // Reaktiivisen lomakkeen setErrors-virhe poistui kentän seuraavassa arvon muutoksessa
    arvonMuutokset(this.lomake.viesti).subscribe(() => this._viestiPuuttuu.set(false))
  }

  onkoTositeValittu(avain: string): boolean {
    return this._valitutTositeAvaimet.has(avain)
  }

  vaihdaTositeValinta(avain: string, valittu: boolean) {
    if (valittu) {
      this._valitutTositeAvaimet.add(avain)
    } else {
      this._valitutTositeAvaimet.delete(avain)
    }
  }

  onkoLaskuValittu(avain: string): boolean {
    return this._valitutLaskuAvaimet.has(avain)
  }

  vaihdaLaskuValinta(avain: string, valittu: boolean) {
    if (valittu) {
      this._valitutLaskuAvaimet.add(avain)
    } else {
      this._valitutLaskuAvaimet.delete(avain)
    }
  }

  tallenna() {
    const selvityspyynto: Selvityspyynto = this.data.kirjausrivi.sp
    if (!selvityspyynto) {
      this._dialogRef.close(false)
      return
    }
    const kysymys = this._stringService.normalizeWhitespace(this.lomake.viesti().value() ?? '').trim()
    if (!kysymys) {
      this._viestiPuuttuu.set(true)
      this.lomake.viesti().markAsTouched()
      return
    }
    selvityspyynto.tyyppi = this.lomake.tyyppi().value()
    selvityspyynto.tyyppiValittu = true
    selvityspyynto.kysymys = kysymys
    selvityspyynto.tositeAvaimet = this.tositteet.filter(tosite => this._valitutTositeAvaimet.has(tosite.avain)).map(tosite => tosite.avain)
    selvityspyynto.laskuAvaimet = this.laskut.filter(lasku => this._valitutLaskuAvaimet.has(lasku.avain)).map(lasku => lasku.avain)
    delete selvityspyynto.liitteetKirjauksesta
    selvityspyynto.paivitetty = this._timestampService.now()
    this._dialogRef.close(true)
  }
}
